import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, Package, Mail, Sparkles } from "lucide-react";
import { formatINR } from "../lib/api";
import { guestOrderLookup } from "../lib/customerApi";
import SafeImage from "../components/SafeImage";

export default function OrderConfirmation() {
  const location = useLocation();
  const initial = location.state?.order || null;
  const [order, setOrder] = useState(initial);

  useEffect(() => {
    if (order) return;
    const params = new URLSearchParams(location.search);
    const n = params.get("n");
    const e = params.get("e");
    if (n && e) guestOrderLookup(n, e).then(setOrder).catch(() => {});
  }, [order, location.search]);

  // Confetti / celebration entrance for the first paint
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  if (!order) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="order-confirmation-loading">
        <p className="eyebrow">Order confirmation</p>
        <h1 className="font-serif-display text-5xl mt-3">Looking up your order…</h1>
      </div>
    );
  }

  const firstName = (order.shipping.full_name || "").split(" ")[0] || "there";
  const orderDate = new Date(order.created_at).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const itemCount = order.items.reduce((s, i) => s + i.qty, 0);

  return (
    <div data-testid="order-confirmation">
      {/* ───── HERO RIBBON — black band ───── */}
      <section className="bg-[#1A1A1A] text-[#c3ba9e] paper-grain">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 relative">
          {/* corner motifs */}
          <Sparkles
            size={20}
            className="absolute top-8 left-6 md:left-12 text-[#c3ba9e]/30 float-soft"
            aria-hidden="true"
          />
          <Sparkles
            size={16}
            className="absolute top-14 right-8 md:right-16 text-[#c3ba9e]/25 float-soft"
            style={{ animationDelay: "1s" }}
            aria-hidden="true"
          />

          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#c3ba9e]/15 border border-[#c3ba9e]/30 flex items-center justify-center">
              <Check size={20} className="text-[#c3ba9e]" />
            </div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#c3ba9e]/70">
              Order received · {orderDate}
            </p>
          </div>

          <h1
            className="font-serif-display text-[2.4rem] sm:text-5xl md:text-[3.6rem] leading-[1.05] mt-6 word-up"
            style={{ animationDelay: "180ms" }}
          >
            Thank you, <em className="italic">{firstName}</em>.
          </h1>

          <p
            className="text-[#c3ba9e]/80 mt-6 leading-relaxed max-w-2xl text-[14.5px] md:text-[15.5px] word-up"
            style={{ animationDelay: "400ms" }}
          >
            Your order has been received at our Ahmedabad atelier. Every piece is hand-finished and
            inspected before it leaves us — we'll email you a tracking link the moment your parcel
            is on its way.
          </p>

          <div
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 word-up"
            style={{ animationDelay: "600ms" }}
          >
            <Stat label="Order number" value={order.order_number} mono />
            <Stat label="Items" value={`${itemCount} piece${itemCount === 1 ? "" : "s"}`} />
            <Stat
              label="Payment"
              value={`${order.payment_method.toUpperCase()} · ${order.payment_status}`}
            />
            <Stat label="Total" value={formatINR(order.totals.total)} display />
          </div>
        </div>
      </section>

      {/* ───── Sealed-letter line ───── */}
      <div className="bg-[#c3ba9e] border-y border-[#1A1A1A]/15 py-3">
        <p className="text-center text-[10px] tracking-[0.32em] uppercase text-[#1A1A1A]/70 flex items-center justify-center gap-3">
          <Mail size={12} />
          A copy of this confirmation has been sent to {order.shipping.email}
          <Mail size={12} className="opacity-0" />
        </p>
      </div>

      {/* ───── ITEMS + SUMMARY ───── */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-12 md:gap-16">
          {/* Items */}
          <div>
            <p className="eyebrow">In your dispatch</p>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-2">
              {itemCount} piece{itemCount === 1 ? "" : "s"}, packed with care.
            </h2>

            <ul className="mt-10 divide-y divide-[#1A1A1A]/15">
              {order.items.map((it, i) => (
                <li key={i} className="py-5 flex gap-5 items-start" data-testid={`oc-item-${i}`}>
                  <SafeImage
                    src={it.image}
                    alt={it.name}
                    className="w-20 h-24 object-cover bg-[#b8af92] flex-shrink-0"
                  />
                  <div className="flex-1">
                    <p className="font-serif-display text-[18px] leading-snug">{it.name}</p>
                    <p className="text-[11px] tracking-[0.18em] uppercase text-[#1A1A1A]/55 mt-2">
                      {it.color} · Size {it.size} · ×{it.qty}
                    </p>
                  </div>
                  <p className="font-serif-display text-[17px] whitespace-nowrap">
                    {formatINR(it.price * it.qty)}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          {/* Right summary */}
          <aside className="space-y-8 md:sticky md:top-28 md:self-start">
            <div className="border border-[#1A1A1A]/20 bg-[#b8af92] p-7">
              <p className="eyebrow">Shipping to</p>
              <p className="font-serif-display text-[17px] mt-3 text-[#1A1A1A]">
                {order.shipping.full_name}
              </p>
              <p className="text-[13.5px] text-[#1A1A1A]/75 leading-relaxed mt-1.5">
                {order.shipping.address_line1}
                {order.shipping.address_line2 && (
                  <>
                    <br />
                    {order.shipping.address_line2}
                  </>
                )}
                <br />
                {order.shipping.city}, {order.shipping.state} {order.shipping.pincode}
                <br />
                {order.shipping.country}
                <br />
                {order.shipping.phone}
              </p>
            </div>

            <div className="border border-[#1A1A1A]/20 p-7 bg-[#c3ba9e]">
              <p className="eyebrow">Payment summary</p>
              <div className="mt-3 space-y-2 text-[13.5px]">
                <Row label="Subtotal" value={formatINR(order.totals.subtotal)} />
                {order.totals.discount_amount > 0 && (
                  <Row
                    label={`Discount (${order.totals.discount_percent}%)`}
                    value={`−${formatINR(order.totals.discount_amount)}`}
                  />
                )}
                <Row
                  label="Shipping"
                  value={order.totals.shipping_fee ? formatINR(order.totals.shipping_fee) : "Complimentary"}
                />
                <div className="border-t border-[#1A1A1A]/30 pt-3 mt-2 flex justify-between items-baseline">
                  <span className="text-[#1A1A1A]">Total</span>
                  <span className="font-serif-display text-[26px] text-[#1A1A1A]">
                    {formatINR(order.totals.total)}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/55 leading-relaxed">
              Need anything? Reply to your confirmation email or write to{" "}
              <a href="mailto:asvasaclothing@gmail.com" className="underline text-[#1A1A1A]">
                asvasaclothing@gmail.com
              </a>
            </div>
          </aside>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap gap-4 mt-14 pt-12 border-t border-[#1A1A1A]/15">
          <Link
            to="/shop"
            className="bg-[#1A1A1A] text-[#c3ba9e] hover:bg-black px-9 py-4 text-[11px] tracking-[0.28em] uppercase transition-colors"
            data-testid="oc-continue-shopping"
          >
            Continue shopping
          </Link>
          <Link
            to="/account"
            className="border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#c3ba9e] px-9 py-4 text-[11px] tracking-[0.28em] uppercase flex items-center gap-2 transition-colors"
            data-testid="oc-view-orders"
          >
            <Package size={14} /> View all orders
          </Link>
        </div>
      </section>

      {/* ───── BRAND CLOSING ───── */}
      <section className="bg-[#1A1A1A] text-[#c3ba9e] py-16 md:py-20 text-center">
        <p className="font-serif-display italic text-2xl md:text-3xl px-6 leading-relaxed max-w-2xl mx-auto">
          "Each Asvasa piece is woven slowly, so that it may live with you for many seasons."
        </p>
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#c3ba9e]/55 mt-6">
          The Asvasa Atelier · Ahmedabad
        </p>
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-[#1A1A1A]/65">{label}</span>
      <span className="text-[#1A1A1A]">{value}</span>
    </div>
  );
}

function Stat({ label, value, mono, display }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.28em] uppercase text-[#c3ba9e]/55 mb-2">{label}</p>
      <p
        className={`text-[#c3ba9e] ${
          display
            ? "font-serif-display text-2xl md:text-3xl"
            : mono
            ? "font-mono text-[13px] md:text-[14px] tracking-[0.08em]"
            : "text-[14px] md:text-[15px]"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
